import { useEffect, useRef } from "react";

/**
 * Ask before closing the tab or navigating away while `isDirty()` is true.
 * `isDirty` is a function so event handlers always read the current form state.
 */
export function useUnsavedChanges(isDirty, message) {
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!isDirty()) return;
      event.preventDefault();
      event.returnValue = message;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty, message]);

  useEffect(() => {
    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      if (!isDirty()) return;
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download"))
        return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:")) return;
      let url;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search
      )
        return;
      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isDirty, message]);

  const stayUrlRef = useRef(window.location.href);

  useEffect(() => {
    stayUrlRef.current = window.location.href;
  });

  useEffect(() => {
    const onPopState = () => {
      if (!isDirty()) return;
      if (window.confirm(message)) return;
      window.history.pushState(null, "", stayUrlRef.current);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isDirty, message]);
}
